import { useCallback, useMemo, useRef } from 'react';
import { getIn } from 'formik';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { CLIENT_ADDRESS_SAME_AS_PROVIDER_FIELD_NAME } from './constants';
import {
  getClientAddressSyncOverridesFromProvider,
  hasProviderAddressFields,
  isClientAddressFieldName,
} from './utils';

import type { RootState } from '../../../../../../../store';
import type {
  GeneralInformationFieldName,
  GeneralInformationFieldValue,
  UseClientProviderAddressSyncOptions,
} from './types';

export default function useClientProviderAddressSync({
  formik,
  saveFieldOnBlur,
  saveGeneralInfoFields,
}: UseClientProviderAddressSyncOptions) {
  const provider = useSelector((state: RootState) => state.providers.provider);
  const pendingUncheckRef = useRef(false);

  const handleSameAsProviderChange = useCallback(
    async (name: GeneralInformationFieldName, value: GeneralInformationFieldValue) => {
      if (name !== CLIENT_ADDRESS_SAME_AS_PROVIDER_FIELD_NAME) {
        return;
      }

      try {
        if (value !== true || provider == null || !hasProviderAddressFields(provider)) {
          await saveFieldOnBlur(name, value);
          return;
        }

        const addressOverrides = getClientAddressSyncOverridesFromProvider(provider);

        addressOverrides.forEach(({ name: overrideName, value: overrideValue }) => {
          formik.setFieldValue(overrideName, overrideValue);
        });

        await saveGeneralInfoFields([{ name, value }, ...addressOverrides]);
      } catch {
        toast.error('Failed to sync client address with provider');
      }
    },
    [formik, provider, saveFieldOnBlur, saveGeneralInfoFields],
  );

  const handleClientAddressFieldChange = useCallback(
    (name: GeneralInformationFieldName) => {
      if (!getIn(formik.values, CLIENT_ADDRESS_SAME_AS_PROVIDER_FIELD_NAME)) {
        return;
      }
      if (!isClientAddressFieldName(name)) {
        return;
      }
      pendingUncheckRef.current = true;
      formik.setFieldValue(CLIENT_ADDRESS_SAME_AS_PROVIDER_FIELD_NAME, false);
    },
    [formik],
  );

  const saveClientAddressFieldOnBlur = useCallback(
    async (name: GeneralInformationFieldName) => {
      if (pendingUncheckRef.current && isClientAddressFieldName(name)) {
        pendingUncheckRef.current = false;
        await saveGeneralInfoFields([
          { name, value: getIn(formik.values, name) },
          { name: CLIENT_ADDRESS_SAME_AS_PROVIDER_FIELD_NAME, value: false },
        ]);
        return;
      }

      await saveFieldOnBlur(name);
    },
    [formik, saveFieldOnBlur, saveGeneralInfoFields],
  );

  const saveClientAddressFieldOnChange = useCallback(
    async (name: GeneralInformationFieldName, value: GeneralInformationFieldValue) => {
      if (!getIn(formik.values, CLIENT_ADDRESS_SAME_AS_PROVIDER_FIELD_NAME)) {
        await saveFieldOnBlur(name, value);
        return;
      }

      if (!isClientAddressFieldName(name)) {
        return;
      }

      pendingUncheckRef.current = false;
      formik.setFieldValue(CLIENT_ADDRESS_SAME_AS_PROVIDER_FIELD_NAME, false);
      await saveGeneralInfoFields([
        { name, value },
        { name: CLIENT_ADDRESS_SAME_AS_PROVIDER_FIELD_NAME, value: false },
      ]);
    },
    [formik, saveFieldOnBlur, saveGeneralInfoFields],
  );

  return useMemo(
    () => ({
      handleSameAsProviderChange,
      handleClientAddressFieldChange,
      saveClientAddressFieldOnBlur,
      saveClientAddressFieldOnChange,
    }),
    [
      handleSameAsProviderChange,
      handleClientAddressFieldChange,
      saveClientAddressFieldOnBlur,
      saveClientAddressFieldOnChange,
    ],
  );
}
